import { useEffect, useState } from 'react'
import { ErroreApi } from '../../servizi/api.ts'
import { Attesa, Avviso } from '../../componenti/Pezzi.tsx'
import { Bottone, Linguette, Modale } from '../../componenti/base.tsx'
import { IconaCopia } from '../../componenti/Icone.tsx'
import { leggiOriginale, type Originale } from '../../servizi/posta-messaggio.ts'
import '../../stile-posta-messaggio.css'

/**
 * «Mostra originale» (539, come in Roundcube): intestazioni complete e sorgente del messaggio, in una finestra.
 *
 * Il messaggio intero non si conserva (raddoppierebbe lo spazio): le intestazioni si', come sono arrivate, per
 * i messaggi arrivati dalla 1.35.0. Per gli altri, e per il sorgente, si mostra la ricostruzione dalle parti
 * archiviate, e la finestra lo dice. Ogni apertura va nel registro come una lettura del messaggio. La stessa
 * finestra c'e' nell'archivio delle caselle: con il testo in Glacier le intestazioni si vedono, il sorgente no.
 */

type Vista = 'intestazioni' | 'sorgente'

export function MostraOriginale({ percorso, chiudi }: {
  /** La rotta dell'originale: della webmail o dell'archivio delle caselle (servizi/posta-messaggio.ts). */
  percorso: string
  chiudi: () => void
}) {
  const [originale, setOriginale] = useState<Originale | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [vista, setVista] = useState<Vista>('intestazioni')
  const [copiato, setCopiato] = useState(false)

  useEffect(() => {
    leggiOriginale(percorso).then(setOriginale)
      .catch((e) => setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.'))
  }, [percorso])

  const testo = originale ? (vista === 'intestazioni' ? originale.intestazioni : originale.sorgente ?? '') : ''

  async function copia() {
    try {
      await navigator.clipboard.writeText(testo)
      setCopiato(true)
      setTimeout(() => setCopiato(false), 2000)
    } catch { /* Senza permesso per gli appunti il testo resta da selezionare a mano. */ }
  }

  return (
    <Modale larga titolo="Originale del messaggio" chiudi={chiudi}>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!originale && !errore && <Attesa testo="Caricamento…" />}
      {originale && (
        <>
          <Linguette<Vista> etichetta="Parti dell’originale" attiva={vista} cambia={(v) => { setVista(v); setCopiato(false) }}
                            voci={[{ valore: 'intestazioni', etichetta: 'Intestazioni' }, { valore: 'sorgente', etichetta: 'Sorgente' }]} />
          <p className="posta-originale-nota">
            {vista === 'intestazioni'
              ? originale.originali
                ? 'Le intestazioni come sono arrivate, con il percorso del messaggio e i controlli dei server. Le copie nascoste (Ccn) non si conservano.'
                : 'Ricostruite dal gestionale: questo messaggio è stato archiviato prima della 1.35.0, o è partito dalla webmail, e le intestazioni originali non ci sono.'
              : 'Ricostruito dal gestionale dalle parti archiviate (intestazioni principali, testo, HTML): il messaggio '
                + 'intero non si conserva. '
                + (originale.allegati
                  ? `${originale.allegati === 1 ? 'L’allegato non è mostrato' : `I ${originale.allegati} allegati non sono mostrati`}: si scaricano dal messaggio, o con «Scarica .eml».`
                  : '')}
            {vista === 'sorgente' && originale.troncato && ' Il testo è lungo e qui si ferma al primo MB.'}
          </p>
          {testo ? (
            <pre className="posta-originale-testo" tabIndex={0} aria-label={vista === 'intestazioni' ? 'Intestazioni' : 'Sorgente'}>
              {testo}
            </pre>
          ) : (
            <Avviso tipo="informazione">
              Il testo del messaggio è in Glacier: il sorgente si vede dopo un risveglio della casella o l’estrazione
              del messaggio. Le intestazioni si vedono già.
            </Avviso>
          )}
          <div className="posta-originale-comandi">
            <Bottone dimensione="piccolo" variante="discreto" icona={<IconaCopia />} disabled={!testo} onClick={() => void copia()}>
              {copiato ? 'Copiato' : 'Copia'}
            </Bottone>
          </div>
        </>
      )}
    </Modale>
  )
}
